import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import {
  FRACTION_ONE,
  FRACTION_ZERO,
} from "@aprao/common/src/math/fraction.js";
import { multiply } from "@aprao/common/src/math/multiply.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { combineNullables } from "@aprao/common/src/util/combineNullables.js";
import { makeAutoObservable } from "mobx";

import {
  additionalIncomePredicate,
  type AppraisalDocumentFacade,
} from "./AppraisalDocumentFacade.js";
import type { AnyRevenueGroupFacade } from "./revenue/AnyRevenueGroupFacade.js";
import type { SalesTaxConfigurationFacade } from "./SalesTaxConfigurationFacade.js";

// tax amount = margin × (1 - 1 / (1 + tax rate))
const calculateIncludedTaxAmount = (
  amountIncludingTax: BigFraction | null,
  rate: BigFraction,
) =>
  multiply([
    amountIncludingTax,
    sum([FRACTION_ONE, opposite(divide(FRACTION_ONE, FRACTION_ONE.add(rate)))]),
  ]);

export class TaxFacade {
  readonly $stores;

  readonly _providers;

  constructor(
    stores: {
      documentFacade: AppraisalDocumentFacade;
      taxConfigurationFacade: SalesTaxConfigurationFacade | null;
    },
    providers: { sitePurchasesPriceTotal: () => BigFraction | null },
  ) {
    makeAutoObservable(this);

    this.$stores = stores;
    this._providers = providers;
  }

  getTaxAmountByRevenueGroup = (group: AnyRevenueGroupFacade) =>
    additionalIncomePredicate(group) ? FRACTION_ZERO : (
      calculateIncludedTaxAmount(
        group.valueGross,
        this.gdvEffectiveSalesTaxRate ?? FRACTION_ZERO,
      )
    );

  getValueNetByRevenueGroup = (group: AnyRevenueGroupFacade) =>
    sum([
      group.valueGross,
      "costsTotal" in group ? opposite(group.costsTotal) : FRACTION_ZERO,
      opposite(this.getTaxAmountByRevenueGroup(group)),
    ]);

  get gdvSalesTaxOnAmount() {
    return combineNullables(
      (salesTaxConfiguration, gdv) => {
        // if not margin scheme then margin equals GDV
        const margin = gdv.sub(
          salesTaxConfiguration.marginSchemeEnabled ?
            (this._providers.sitePurchasesPriceTotal() ?? FRACTION_ZERO)
          : FRACTION_ZERO,
        );

        return calculateIncludedTaxAmount(margin, salesTaxConfiguration.value);
      },
      this.$stores.taxConfigurationFacade,
      this.$stores.documentFacade.stages.revenue.getGrossDevelopmentValue(
        "any",
      ),
    );
  }

  get gdvEffectiveSalesTaxRate() {
    // effective tax rate = tax amount / (GDV - tax amount)
    return divide(
      this.gdvSalesTaxOnAmount,
      sum([
        this.$stores.documentFacade.stages.revenue.getGrossDevelopmentValue(
          "any",
        ),
        opposite(this.gdvSalesTaxOnAmount),
      ]),
    );
  }

  get netDevelopmentValue() {
    return sum([
      this.$stores.documentFacade.stages.revenue.getGrossDevelopmentValue(
        "any",
      ),
      opposite(this.$stores.documentFacade.salesCostTotal),
      opposite(this.gdvSalesTaxOnAmount),
    ]);
  }

  get netRevenue() {
    return sum([
      this.netDevelopmentValue,
      this.$stores.documentFacade.getAdditionalIncomeTotal("any"),
    ]);
  }
}
